import React from 'react';
import {
  Box,
  Typography,
  Chip
} from '@mui/material';
import { CompanyImage, CompanyImageType } from '../../../../types/models';

interface DraggableLogoProps {
  image: CompanyImage;
  onDragStart?: (image: CompanyImage) => void;
}

const ImageTypeColors: Record<CompanyImageType, 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'> = {
  [CompanyImageType.LOGO]: 'primary',
  [CompanyImageType.HEADER]: 'secondary',
  [CompanyImageType.SIGNATURE]: 'warning',
  [CompanyImageType.WATERMARK]: 'info',
  [CompanyImageType.LETTERHEAD]: 'success',
  [CompanyImageType.OTHER]: 'error'
};

export const DraggableLogo: React.FC<DraggableLogoProps> = ({
  image,
  onDragStart
}) => {
  const handleDragStart = (e: React.DragEvent) => {
    // Set the data to be transferred during drag - optimized for PDF editor
    const dragData = {
      type: 'company-image',
      imageUrl: image.url,
      fileName: image.fileName,
      width: image.width || 320,
      height: image.height || 240,
      imageType: image.imageType,
      imageId: image.id
    };

    console.log('🔄 Company image drag started:', dragData);
    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
    e.dataTransfer.setData('text/plain', image.url); // Fallback for simple drops
    e.dataTransfer.effectAllowed = 'copy';

    if (onDragStart) {
      onDragStart(image);
    }
  };

  return (
      <Box
        draggable
        onDragStart={handleDragStart}
        sx={{
          position: 'relative',
          width: '100%',
          height: 80,
          border: '2px solid',
          borderColor: 'grey.300',
          borderRadius: 2,
          p: 1,
          cursor: 'grab',
          transition: 'all 0.2s',
          backgroundColor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          overflow: 'hidden',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: 2,
            backgroundColor: 'action.hover'
          },
          '&:active': {
            cursor: 'grabbing',
            transform: 'scale(0.98)'
          }
        }}
      >
        {/* Image Thumbnail */}
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: 1,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'grey.100',
            flexShrink: 0
          }}
        >
          <img
            src={image.url} // Updated from imageUrl
            alt={image.fileName} // Updated from imageName
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            draggable={false}
          />
        </Box>

        {/* Image Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontWeight: 500,
              color: 'text.primary',
              mb: 0.5
            }}
            title={image.fileName} // Updated from imageName
          >
            {image.fileName} {/* Updated from imageName */}
          </Typography>
          <Chip
            label={image.imageType}
            size="small"
            color={ImageTypeColors[image.imageType]}
            sx={{
              fontSize: '0.7rem',
              height: 20
            }}
          />
        </Box>
      </Box>
  );
};
